import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { EjariProperty, Lookup } from "@eres/ejari-api/Entity";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { AlphaSortProcessor, populateLookupOnTo, StateConsumer } from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import FormSection from "@my-dld/components/Form/FormSection";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import FormRow from "@eres/forms-web/FormRow";
import { IdExtractor } from "@my-dld/utils/LookupUtil";
import PickerInput from "@eres/forms-web/PickerInput";
import TextInputField from "@eres/forms-web/TextInputField";
import NumberConverter from "@eres/forms/converters/NumberConverter";
import FormAction from "@my-dld/components/Form/FormAction";
import { TenantPropertySearch } from "@eres/ejari-api/IApi";
import { navigate } from "@my-dld/utils/StoreUtils";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { EjariApi } from "@eres/ejari-api";
import { resetTenantContext, TenantContext, updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import PageContent from "@my-dld/components/PageContent";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../utils";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import PropertyResultListView from "./PropertyResultListView";
import PropertyResultGridView from "./PropertyResultGridView";

interface Props extends RoutedViewProps {

}

interface LookupState {
	SearchByTypes?: Lookup[];
	PropertyTypes?: Lookup[];
	Areas?: Lookup[];
	Years?: Lookup[];
}

export interface TenantPropertySearchState extends LookupState {
	searchBy?: Lookup;
	munNo?: string;
	munSubNo?: string;
	propertyType?: Lookup;
	unitNo?: string;
	buildingName?: string;
	villaName?: string;
	landNo?: string;
	landSubNo?: string;
	area?: Lookup;
	certificateNo?: string;
	certificateYear?: Lookup;
}

interface State extends TenantPropertySearchState, LoadableDataState {
	properties: EjariProperty[];
}

class SearchProperty extends Component<Props, State> {
	static contextType = TenantContext;
	context!: React.ContextType<typeof TenantContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			SearchByTypes: SearchByTypes,
			PropertyTypes: PropertyTypes,
			Years: getYears(),
			errorContainer: {},
			loadState: "loading",
			properties: [],
		};
	}

	componentDidMount() {
		if ( this.context.searchState ) {
			this.setState( {
				...this.context.searchState,
			} );
		} else {
			populateLookupOnTo( EjariLookUp.AREAS, StateConsumer( this, "Areas" ), undefined, AlphaSortProcessor );
		}
	}

	render() {
		const { SearchByTypes, loadState, properties } = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
		};
		const subFiledProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
		};
		return <>
			<PageContent
				title={ Strings.register_certificate }
				hideQLinks={ true }>
				<PageTitle title={ Strings.register_certificate } paths={ [ DashboardBCItem ] }/>
				<div className="tender-registration-form">
					<FormSection title={ Strings.search_property }>
						<div className="mandatory-field-info">
							<small>{ Strings.mandatory_fields_note }</small>
						</div>
						<FormRow { ...filedProps }>
							<PickerInput
								required={ true }
								id={ "searchBy" }
								label={ Strings.search_by }
								isLoading={ SearchByTypes === undefined }
								items={ SearchByTypes }
								toIdentifier={ IdExtractor }/>
						</FormRow>
						<RenderMunicipalityNoSearch filedProps={ subFiledProps } state={ this.state }/>
						<RenderLandNoSearch filedProps={ subFiledProps } state={ this.state }/>
						<RenderTitleDeedSearch filedProps={ subFiledProps } state={ this.state }/>
					</FormSection>
					<FormRow>
						<FormAction
							primaryActionLabel={ Strings.search }
							onPrimaryAction={ this.onSearch }
							secondaryActionLabel={ Strings.reset }
							onSecondaryAction={ this.onReset }
							actionContainerClassName={ "col-md-12" }/>
					</FormRow>
				</div>
				{ loadState === "done" && <ListGridDataView
					isLoading={ false }
					dataAvailable={ properties.length > 0 }
					title={ Strings.properties }
					renderListView={ this.renderListView }
					renderGridView={ this.renderGridView }
					containerClass={ "" }/> }
			</PageContent>
		</>;
	}

	private renderListView = () => {
		const { propertyType, properties } = this.state;
		return <PropertyResultListView
			propertyType={ propertyType!.Id }
			properties={ properties }
			onDetails={ this.onPropertySelect }/>;
	};

	private renderGridView = () => {
		const { propertyType, properties } = this.state;
		return <PropertyResultGridView
			propertyType={ propertyType!.Id }
			properties={ properties }
			onDetails={ this.onPropertySelect }/>;
	};

	private onSearch = () => {
		if ( this.isValid() ) {
			showLoading();
			this.setState( { loadState: "loading" } );
			EjariApi.searchTenantProperty( this.getRequest() ).then( ( resp: any ) => {
				hideLoading();
				this.onSearchResults( resp );
			} ).catch( e => {
				hideLoading();
				showErrorMessage( e );
			} );
		}
	};

	private onReset = () => {
		this.setState( {
			searchBy: undefined,
			munNo: undefined,
			munSubNo: undefined,
			propertyType: undefined,
			unitNo: undefined,
			buildingName: undefined,
			villaName: undefined,
			landNo: undefined,
			landSubNo: undefined,
			area: undefined,
			certificateNo: undefined,
			certificateYear: undefined,
			loadState: "loading",
			errorContainer: {},
		} );
	};

	private isValid() {
		const { searchBy, propertyType, area, certificateYear } = this.state;
		let isValid = true;
		const error: ErrorContainer = {};
		if ( searchBy == undefined ) {
			isValid = false;
			error.searchBy = Strings.required;
		}
		if ( searchBy ) {
			if ( searchBy.Id === MUNICIPALITY_NO.Id ) {
				isValid = checkNonEmpty( this.state, "munNo", error ) && isValid;
				isValid = checkNonEmpty( this.state, "munSubNo", error ) && isValid;
			} else if ( searchBy.Id === LAND_NO.Id ) {
				isValid = checkNonEmpty( this.state, "landNo", error ) && isValid;
				isValid = checkNonEmpty( this.state, "landSubNo", error ) && isValid;
				if ( area == undefined ) {
					isValid = false;
					error.area = Strings.required;
				}
			} else if ( searchBy.Id === TITLE_DEED.Id ) {
				isValid = checkNonEmpty( this.state, "certificateNo", error ) && isValid;
				if ( certificateYear == undefined ) {
					isValid = false;
					error.certificateYear = Strings.required;
				}
			}
			if ( propertyType == undefined ) {
				isValid = false;
				error.propertyType = Strings.required;
			}
			if ( searchBy.Id !== TITLE_DEED.Id ) {
				if ( propertyType && propertyType.Id === UNIT.Id ) {
					isValid = checkNonEmpty( this.state, "unitNo", error ) && isValid;
					isValid = checkNonEmpty( this.state, "buildingName", error ) && isValid;
				} else if ( propertyType && propertyType.Id === VILLA.Id ) {
					isValid = checkNonEmpty( this.state, "villaName", error ) && isValid;
				}
			}
		}
		this.setState( {
			errorContainer: error,
		} );
		return isValid;
	}

	private getRequest(): TenantPropertySearch {
		const {
			searchBy, munNo, munSubNo, propertyType, unitNo, buildingName, villaName, landNo, landSubNo, area,
			certificateNo, certificateYear
		} = this.state;
		const req: TenantPropertySearch = {};
		if ( searchBy!.Id === MUNICIPALITY_NO.Id || searchBy!.Id === LAND_NO.Id ) {
			if ( searchBy!.Id === MUNICIPALITY_NO.Id ) {
				req.DMNo = munNo;
				req.DmSubNo = munSubNo;
			} else if ( searchBy!.Id === LAND_NO.Id ) {
				req.LandNo = landNo;
				req.LandSubno = landSubNo;
				req.AreaId = area!.Id;
			}
			if ( propertyType!.Id === UNIT.Id ) {
				req.PropertyNo = unitNo;
				req.BuildingName = buildingName;
			} else if ( propertyType!.Id === VILLA.Id ) {
				req.BuildingName = villaName;
			}
		} else if ( searchBy!.Id === TITLE_DEED.Id ) {
			req.CertificateNumber = certificateNo;
			req.CertificateYear = certificateYear!.Id;
		}
		req.PropertyTypeId = propertyType!.Id;
		return req;
	}

	private onSearchResults( properties: EjariProperty[] ) {
		resetTenantContext();
		if ( properties.length === 1 ) {
			updateTenantContext( { searchState: this.state, property: properties[ 0 ] } );
			navigate( "details" );
		} else {
			this.setState( {
				properties: properties,
				loadState: "done",
			} );
		}
	}

	private onPropertySelect = ( property: EjariProperty ) => {
		updateTenantContext( { searchState: this.state, property: property } );
		navigate( "details" );
	};
}

function RenderMunicipalityNoSearch( props: { filedProps: Partial<WebFormFieldProps<any, any>>, state: State } ) {
	const { filedProps, state } = props;
	const { searchBy, PropertyTypes, propertyType } = state;
	if ( searchBy && searchBy.Id === MUNICIPALITY_NO.Id ) {
		return <FormRow { ...filedProps }>
			<TextInputField
				required={ true }
				id={ "munNo" }
				label={ Strings.municipality_number }
				valueConverter={ NumberConverter }/>
			<TextInputField
				required={ true }
				id={ "munSubNo" }
				label={ Strings.mun_sub_number }
				valueConverter={ NumberConverter }/>
			<PickerInput
				required={ true }
				id={ "propertyType" }
				label={ Strings.property_type }
				isLoading={ PropertyTypes === undefined }
				items={ PropertyTypes }
				toIdentifier={ IdExtractor }/>
			{ propertyType && propertyType.Id === UNIT.Id && <TextInputField
				required={ true }
				id={ "unitNo" }
				label={ Strings.Unit_Number_Label }/> }
			{ propertyType && propertyType.Id === UNIT.Id && <TextInputField
				required={ true }
				id={ "buildingName" }
				label={ Strings.building_name }/> }
			{ propertyType && propertyType.Id === VILLA.Id && <TextInputField
				required={ true }
				id={ "villaName" }
				label={ Strings.villa_name }/> }
		</FormRow>;
	}
	return <div/>;
}

function RenderLandNoSearch( props: { filedProps: Partial<WebFormFieldProps<any, any>>, state: State } ) {
	const { filedProps, state } = props;
	const { searchBy, Areas, PropertyTypes, propertyType } = state;
	if ( searchBy && searchBy.Id === LAND_NO.Id ) {
		return <FormRow { ...filedProps }>
			<TextInputField
				required={ true }
				id={ "landNo" }
				label={ Strings.land_number }
				valueConverter={ NumberConverter }/>
			<TextInputField
				required={ true }
				id={ "landSubNo" }
				label={ Strings.mun_sub_number }
				valueConverter={ NumberConverter }/>
			<PickerInput
				required={ true }
				id={ "area" }
				label={ Strings.area }
				isLoading={ Areas === undefined }
				items={ Areas }
				toIdentifier={ IdExtractor }/>
			<PickerInput
				required={ true }
				id={ "propertyType" }
				label={ Strings.property_type }
				isLoading={ PropertyTypes === undefined }
				items={ PropertyTypes }
				toIdentifier={ IdExtractor }/>
			{ propertyType && propertyType.Id === UNIT.Id && <TextInputField
				required={ true }
				id={ "unitNo" }
				label={ Strings.Unit_Number_Label }/> }
			{ propertyType && propertyType.Id === UNIT.Id && <TextInputField
				required={ true }
				id={ "buildingName" }
				label={ Strings.building_name }/> }
			{ propertyType && propertyType.Id === VILLA.Id && <TextInputField
				required={ true }
				id={ "villaName" }
				label={ Strings.villa_name }/> }
		</FormRow>;
	}
	return <div/>;
}

function RenderTitleDeedSearch( props: { filedProps: Partial<WebFormFieldProps<any, any>>, state: State } ) {
	const { filedProps, state } = props;
	const { searchBy, Years, PropertyTypes } = state;
	if ( searchBy && searchBy.Id === TITLE_DEED.Id ) {
		return <FormRow { ...filedProps }>
			<TextInputField
				required={ true }
				id={ "certificateNo" }
				label={ Strings.certificate_number }
				valueConverter={ NumberConverter }/>
			<PickerInput
				required={ true }
				id={ "certificateYear" }
				label={ Strings.certificate_year }
				isLoading={ Years === undefined }
				items={ Years }
				toIdentifier={ IdExtractor }/>
			<PickerInput
				required={ true }
				id={ "propertyType" }
				label={ Strings.property_type }
				isLoading={ PropertyTypes === undefined }
				items={ PropertyTypes }
				toIdentifier={ IdExtractor }/>
		</FormRow>;
	}
	return <div/>;
}

const MUNICIPALITY_NO: Lookup = {
	Id: 1, ...LVStrings.municipality_number,
};
const LAND_NO: Lookup = {
	Id: 2, ...LVStrings.land_number,
};
const TITLE_DEED: Lookup = {
	Id: 3, ...LVStrings.title_deed,
};
const SearchByTypes: Lookup[] = [ MUNICIPALITY_NO, LAND_NO, TITLE_DEED ];
export const UNIT: Lookup = {
	Id: 2, ...LVStrings.unit,
};
export const VILLA: Lookup = {
	Id: 4, ...LVStrings.villa,
};
const PropertyTypes: Lookup[] = [ UNIT, VILLA ];

function getYears() {
	const years: Lookup[] = [], startYear = 1970;
	let currentYear = new Date().getFullYear();
	while ( startYear <= currentYear ) {
		years.push( { Id: currentYear, en: "" + currentYear, ar: "" + currentYear } );
		currentYear--;
	}
	return years;
}

export default SearchProperty;
